import { Dialog, Flex, Button, Text, Heading } from '@radix-ui/themes';

import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DialogHeader } from 'shared/components';
import { ContactSupportIcon } from 'shared/icons';
import { HttpClient } from 'shared/libs/http';
import { AsyncState } from 'shared/libs/state';
import { ApiError } from 'shared/types';
import { toast } from 'sonner';
import {
   PlansOrderRequestPayload,
   PlansOrderResponse,
   PurchasePlan,
} from '../plans-purchase.types';

// Якщо price 0 , cлати запит тількі id https://grc.net.ua/api/plans/order і перекинути /user/paymenthistory

type StoreCallbacks = {
   onError?: (message: string) => void;
   onAuthError?: () => void;
};

class PromoPlanPurchaseStore {
   readonly orderPlanState = new AsyncState<PlansOrderResponse>();
   constructor() {
      makeAutoObservable(this, undefined, {
         autoBind: true,
      });
   }

   get ordering(): boolean {
      return this.orderPlanState.status === 'pending';
   }

   async orderPlan({
      planId,
      onSuccess,
      onError,
      onAuthError,
   }: StoreCallbacks & {
      planId: number;
      onSuccess?: (message?: string, response?: PlansOrderResponse) => void;
   }): Promise<void> {
      const payload: PlansOrderRequestPayload = {
         id: planId,
      };

      await this.orderPlanState.run(
         () => HttpClient.post<PlansOrderResponse>('/plans/order', payload),
         {
            onSuccess: (resp) => {
               if (resp.status === 200) {
                  onSuccess?.(resp.message, resp);
                  return;
               }

               if (this.isAuthMessage(resp.message)) {
                  onAuthError?.();
                  return;
               }

               onError?.(resp.message || 'Could not order membership plan');
            },
            onError: (error) => {
               if (this.shouldTreatAsAuthError(error)) {
                  onAuthError?.();
                  return;
               }

               onError?.(error.message);
            },
         }
      );
   }

   private isAuthMessage(message?: string): boolean {
      return !!message && /token has expired/i.test(message);
   }

   private shouldTreatAsAuthError(error: ApiError): boolean {
      return this.isAuthMessage(error.message) || error.message === 'No response from server';
   }
}

function PromoPlanDialogView({
   plan,
   open,
   setOpen,
}: {
   plan: PurchasePlan;
   open: boolean;
   setOpen: (o: boolean) => void;
}) {
   const navigate = useNavigate();
   const [planPurchase] = useState(() => new PromoPlanPurchaseStore());

   return (
      <Dialog.Content maxWidth="500px">
         <Flex justify="center">
            <ContactSupportIcon color="red" width={80} height={80}></ContactSupportIcon>
         </Flex>

         <Heading align="center" color="gray">
            Готові розпочати?
         </Heading>

         <Flex mt="4">
            <Text align="center" size="4">
               Послуги доступні протягом терміну дії пакету, а невикористані сервіси будуть
               відключені після його закінчення.
            </Text>
         </Flex>

         <Flex justify="center" mt="6" gap="5">
            <Dialog.Close>
               <Button size="3" variant="outline" color="gray" radius="small">
                  Ні
               </Button>
            </Dialog.Close>

            <Button
               size="3"
               radius="small"
               loading={planPurchase.ordering}
               onClick={() => {
                  planPurchase.orderPlan({
                     planId: plan.plan_id,
                     onSuccess: async (_, response) => {
                        if (response?.response) {
                           setOpen(false);
                           navigate('/user/paymenthistory');
                           toast.success(
                              'Ваш пакет послуг активовано, і ви можете перевірити його деталі у вашому профілі.'
                           );
                        }
                     },
                     onError: (message) => {
                        toast.error('Щось пійшло не так, спробуйте пізніше');
                     },
                  });
               }}
            >
               Так
            </Button>
         </Flex>
      </Dialog.Content>
   );
}

export const PromoPlanDialog = observer(PromoPlanDialogView);
